// Base API client configuration

/**
 * Get the base API URL from environment variables or use a fallback
 */
export const getApiBaseUrl = (): string => {
  // Server-side
  if (typeof window === 'undefined') {
    return process.env.VITE_API_URL || import.meta.env.VITE_API_URL || "http://localhost:8080";
  }
  // First check for environment variable
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Default to local development if no environment variable is set
  return "http://localhost:8080";
};

/**
 * Creates standard headers for API requests
 */
export const getHeaders = (includeAuth = true) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // Include auth token if available and requested
  if (includeAuth && typeof window !== 'undefined') {
    const storageKey =
      import.meta.env.VITE_AUTH_STORAGE_KEY || "yetfarrag_auth";
    const authData = localStorage.getItem(storageKey);

    if (authData) {
      try {
        const { token } = JSON.parse(authData);
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error("Error parsing auth data", error);
      }
    }
  }

  return headers;
};

/**
 * Generic API error type
 */
export type ApiError = {
  message: string;
  status?: number;
  details?: unknown;
};

/**
 * Standard API response format
 */
export type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: ApiError;
};

/**
 * Handle API errors consistently
 */
export const handleApiError = (error: unknown): ApiError => {
  console.error("API Error:", error);

  if (error instanceof Response) {
    return {
      message: `Request failed with status ${error.status}`,
      status: error.status,
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message || "Unknown error occurred",
      details: error,
    };
  }

  return {
    message: "Unknown error occurred",
    details: error,
  };
};
